import React, { useState, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import styled from '@emotion/styled'
import { errors } from 'utils/error-handling/errors'
import Card from 'legacy/components/Card/Card'
import Tabs from 'legacy/components/Tabs'
import { type ChartsData, type TimeRange } from 'legacy/components/DataViz/dataViz.types'
import LineChart from 'legacy/components/DataViz/Charts/LineChart'
import BarChart from 'legacy/components/DataViz/Charts/BarChart'
import ChartFooter from 'legacy/components/DataViz/Charts/ChartFooter/ChartFooter'
import DownloadChartCta from 'legacy/components/DataViz/Charts/DownloadChartCta'
import Link from 'legacy/components/Link'
import AccessControl from 'legacy/components/AccessControl/AccessControl'
import { Permission } from 'legacy/common/types'
import { type AccessControlConfig } from 'legacy/components/AccessControl/accessControl.utils'

const Container = styled.div<{ margin?: string }>`
  margin: ${({ margin }) => margin || '0'};
`

interface Props {
  charts: ChartsData[]
  timeRange: TimeRange
  action?: string
  title?: string
  downloadable?: boolean
}

function getChartComponent(type: string) {
  switch (type) {
    case 'line_chart':
      return LineChart
    case 'bar_chart':
      return BarChart
    default:
      return null
  }
}

function getRoute(action: string) {
  switch (action) {
    case 'view_performance_metrics':
      return 'performance-metrics'

    default: {
      errors.captureMessage(`Unsupported Dynamic Action: ${action}`)

      return 'dashboard'
    }
  }
}

function getLinkAccess(action?: string): AccessControlConfig {
  switch (action) {
    case 'view_performance_metrics':
      return { permissions: Permission.Reports }
    default:
      return {}
  }
}

const ChartContainer: React.FC<Props> = props => {
  const [activeChart, setActiveChart] = useState<string>(props.charts[0].title_key)

  const { charts, tabs } = useMemo(
    () => ({
      charts: props.charts.reduce<{ [key: string]: ChartsData }>(
        (chartsMap, chart) => ({
          ...chartsMap,
          [chart.title_key]: chart,
        }),
        {}
      ),
      tabs: props.charts.map(({ title_key }) => ({
        label: title_key,
        value: title_key,
      })),
    }),
    [props.charts]
  )

  const chart = charts[activeChart]
  const ChartComponent = getChartComponent(chart.type)

  const title = props.charts.length === 1 ? props.charts[0].title_key : props.title

  return (
    ChartComponent && (
      <Container data-testid="dataviz-chart-container" margin="20px 0">
        <Card>
          <Card.Header title={title && <FormattedMessage id={title} />}>
            {tabs.length > 1 && (
              <Tabs
                fillHeight
                onClick={(selectedChart: string) => {
                  setActiveChart(selectedChart)
                }}
                selected={activeChart}
                tabs={tabs}
              />
            )}
            {props.action && (
              <AccessControl {...getLinkAccess(props.action)}>
                <Link route={getRoute(props.action)}>
                  <FormattedMessage id={props.action} />
                </Link>
              </AccessControl>
            )}
          </Card.Header>
          <Card.Body padding="0">
            <ChartComponent data={chart.data} series={chart.series} dataType={chart.data_type} />
          </Card.Body>
          <Card.Footer>
            <ChartFooter chartSeries={chart.series} timeRange={props.timeRange} />
            {props.downloadable && <DownloadChartCta chartData={charts} chartTitle={title} />}
          </Card.Footer>
        </Card>
      </Container>
    )
  )
}

export default ChartContainer
